// Salidas con "Valor pendiente": productos entregados sin valor conocido (NO es Q0.00). Administración completa el
// valor total de cada producto; el sistema calcula el valor unitario (total ÷ cantidad) y suma lo valorado a la deuda.
import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { listSalidasPendientesValor, valorarSalidaProductos } from '../../services/data';
import { toMoney } from '../../utils/numbers';
import { formatDateTime } from '../../utils/dates';
import MoneyInput from '../common/MoneyInput';
import '../finanzas/ui.css';
import FondoModal from '../common/FondoModal';

const fechaHora = formatDateTime;
const cant = (v) => Number(v).toLocaleString('es-GT', { maximumFractionDigits: 3 });

/** Productos de una salida: Producto | Cantidad | Descripción | Total | Unitario | Estado de valoración. */
export function TablaProductosSalida({ salida: s }) {
  return (
    <div className="ces-scroll"><table className="fx-table fx-table--stack">
      <thead><tr><th>Producto</th><th className="num">Cantidad</th><th>Descripción</th><th className="num">Unitario</th><th className="num">Total</th><th>Estado</th></tr></thead>
      <tbody>{s.items.map((i) => <tr key={i.id}>
        <td data-label="Producto">{i.producto}</td><td data-label="Cantidad" className="num">{cant(i.cantidad)}</td><td data-label="Descripción">{i.detalleCantidad || '—'}</td>
        <td data-label="Unitario" className="num">{i.valorUnitario == null ? '—' : toMoney(i.valorUnitario)}</td>
        <td data-label="Total" className="num">{i.total == null ? '—' : toMoney(i.total)}</td>
        <td data-label="Estado">{i.total == null ? <span className="fx-badge fx-badge--hoy">Valor pendiente</span>
          : <span className="fx-badge fx-badge--completado" title={i.valoradoPor ? `Valor completado por ${i.valoradoPor} · ${fechaHora(i.valoradoAt)}` : undefined}>Valorado</span>}</td>
      </tr>)}</tbody>
      <tfoot><tr><td colSpan={4}><b>Total general de la salida</b>{s.valorPendiente && <small> · {s.productosPendientes} con valor pendiente</small>}</td>
        <td className="num"><b>{toMoney(s.total)}</b></td><td /></tr></tfoot>
    </table></div>
  );
}

/** Completar el valor total de los productos pendientes (los que se dejen vacíos siguen pendientes). */
export function ValorarSalidaModal({ salida: s, onClose, onSaved }) {
  const pendientes = s.items.filter((i) => i.total == null);
  const [valores, setValores] = useState(() => Object.fromEntries(pendientes.map((i) => [i.id, ''])));
  const [busy, setBusy] = useState(false);
  const llenos = Object.values(valores).filter((v) => String(v).trim() !== '');
  const guardar = async (e) => {
    e.preventDefault();
    if (!llenos.length) return Swal.fire('Completar valor', 'Indica el valor total de al menos un producto.', 'warning');
    setBusy(true);
    try {
      const r = await valorarSalidaProductos(s.id, Object.entries(valores).map(([itemId, total]) => ({ itemId, total })));
      Swal.fire({ icon: 'success', title: 'Valor completado', timer: 2000, showConfirmButton: false,
        text: `${r.destino} debe ${toMoney(r.total)} a ${r.origen}.${r.valorPendiente ? ` Aún quedan ${r.productosPendientes} con valor pendiente.` : ''}` });
      await onSaved?.(r);
    } catch (x) { Swal.fire('No se pudo guardar', x.message || '', 'error'); }
    finally { setBusy(false); }
  };
  return (
    <FondoModal onClose={onClose} style={{ zIndex: 1350 }}>
      <form className="fx-modal fx-modal--sm" role="dialog" aria-modal="true" aria-label="Completar valor" onSubmit={guardar} onMouseDown={(e) => e.stopPropagation()}>
        <header><div><h2>Completar valor</h2><p>{s.origen} → {s.destino} · {fechaHora(s.fechaAt)}</p></div><button type="button" aria-label="Cerrar" onClick={onClose}>×</button></header>
        <div className="vp-lista">
          {pendientes.map((i) => {
            const v = valores[i.id];
            const unit = String(v).trim() !== '' && Number(i.cantidad) > 0 ? Math.round((Number(v) / Number(i.cantidad)) * 100) / 100 : null;
            return <label key={i.id} className="vp-fila"><span><b>{i.producto}</b><small>{cant(i.cantidad)}{i.detalleCantidad ? ` · ${i.detalleCantidad}` : ''}</small></span>
              <span className="vp-monto"><MoneyInput style={{ width: 140 }} value={v} aria-label={`Valor total de ${i.producto}`}
                onChange={(x) => setValores({ ...valores, [i.id]: x })} />
                <small>{unit == null ? 'Vacío = sigue pendiente' : `Unitario ${toMoney(unit)}`}</small></span></label>;
          })}
        </div>
        <p className="fx-empty" style={{ padding: '8px 0 0' }}>Lo valorado se suma a la deuda de {s.destino} con {s.origen} (una sola cuenta por salida). Q0.00 significa que no tiene costo.</p>
        <div className="fx-actions"><button type="button" className="fx-btn" onClick={onClose}>Cancelar</button>
          <button className="fx-btn fx-btn--primary" disabled={busy}>{busy ? 'Guardando…' : 'Guardar valores'}</button></div>
      </form>
    </FondoModal>
  );
}

/** Lista de salidas con productos de valor pendiente (Administración). */
export default function SalidasPendientesValor({ sucursalId, onCambio }) {
  const [lista, setLista] = useState(null);
  const [valorando, setValorando] = useState(null);
  const [abierta, setAbierta] = useState(null);
  const cargar = useCallback(() => {
    if (!sucursalId) return;
    listSalidasPendientesValor(sucursalId).then(setLista).catch(() => setLista([]));
  }, [sucursalId]);
  useEffect(() => { cargar(); }, [cargar]);
  if (!lista?.length) return null;
  return (
    <section className="fx-card vp-seccion">
      <h2>Salidas con valor pendiente <span className="fx-badge fx-badge--hoy">{lista.length}</span></h2>
      <p className="fx-empty" style={{ padding: 0 }}>Productos entregados sin valor conocido. La deuda solo incluye lo valorado hasta que se complete.</p>
      <ul className="vp-salidas">{lista.map((s) => <li key={s.id}>
        <div className="vp-salida-hd">
          <span><b>{s.origen} → {s.destino}</b><small>{fechaHora(s.fechaAt)} · {s.productosPendientes} pendiente(s) · valorado {toMoney(s.total)}</small></span>
          <span className="vp-acc"><button type="button" className="fx-link" onClick={() => setAbierta(abierta === s.id ? null : s.id)}>{abierta === s.id ? 'Ocultar' : 'Ver productos'}</button>
            <button type="button" className="fx-btn fx-btn--primary" onClick={() => setValorando(s)}>Completar valor</button></span>
        </div>
        {abierta === s.id && <TablaProductosSalida salida={s} />}
      </li>)}</ul>
      {valorando && <ValorarSalidaModal salida={valorando} onClose={() => setValorando(null)}
        onSaved={() => { setValorando(null); cargar(); onCambio?.(); }} />}
    </section>
  );
}
